import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { adminCustomersAPI } from '../services/adminApi'

interface CustomerSubscription {
  status: string
  currentPeriodEnd: string | null
  stripeSubscriptionId: string | null
  billingPeriod: string | null
  tier: string | null
  startDate: string | null
  cancelledAt: string | null
}

interface Customer {
  userId: string
  email: string
  tier: string
  credits: number
  stripeCustomerId: string | null
  registrationDate: string
  lastModified: string
  enabled: boolean
  status: string
  subscription: CustomerSubscription | null
  nextBillingDate: string | null
  unlockCount: number
}

interface AdminCustomersState {
  customers: Customer[]
  selectedCustomer: Customer | null
  loading: boolean
  error: string | null
  lastFetched: number | null
}

const initialState: AdminCustomersState = {
  customers: [],
  selectedCustomer: null,
  loading: false,
  error: null,
  lastFetched: null
}

// Async thunks
export const fetchCustomers = createAsyncThunk(
  'adminCustomers/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await adminCustomersAPI.listCustomers()
      return response.customers
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch customers')
    }
  }
)

export const fetchCustomerDetails = createAsyncThunk(
  'adminCustomers/fetchDetails',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await adminCustomersAPI.getCustomer(userId)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch customer details')
    }
  }
)

export const addCustomerCredits = createAsyncThunk(
  'adminCustomers/addCredits',
  async (
    { userId, credits, reason }: { userId: string; credits: number; reason?: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await adminCustomersAPI.addCredits(userId, credits, reason)
      // Refresh the customer list after adding credits
      dispatch(fetchCustomers())
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to add credits')
    }
  }
)

export const updateCustomerTier = createAsyncThunk(
  'adminCustomers/updateTier',
  async (
    { userId, tier }: { userId: string; tier: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await adminCustomersAPI.updateTier(userId, tier)
      // Refresh the customer list after updating tier
      dispatch(fetchCustomers())
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update tier')
    }
  }
)

export const updateCustomerStatus = createAsyncThunk(
  'adminCustomers/updateStatus',
  async (
    { userId, enabled }: { userId: string; enabled: boolean },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await adminCustomersAPI.updateStatus(userId, enabled)
      // Refresh the customer list after updating status
      dispatch(fetchCustomers())
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update status')
    }
  }
)

export const deleteCustomer = createAsyncThunk(
  'adminCustomers/delete',
  async (userId: string, { rejectWithValue, dispatch }) => {
    try {
      const response = await adminCustomersAPI.deleteCustomer(userId)
      // Refresh the customer list after deletion
      dispatch(fetchCustomers())
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete customer')
    }
  }
)

export const resetCustomerPassword = createAsyncThunk(
  'adminCustomers/resetPassword',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await adminCustomersAPI.resetPassword(userId)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to reset password')
    }
  }
)

const adminCustomersSlice = createSlice({
  name: 'adminCustomers',
  initialState,
  reducers: {
    clearAdminCustomersError: (state) => {
      state.error = null
    },
    clearSelectedCustomer: (state) => {
      state.selectedCustomer = null
    }
  },
  extraReducers: (builder) => {
    // Fetch customers
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false
        state.customers = action.payload
        state.lastFetched = Date.now()
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Fetch customer details
    builder
      .addCase(fetchCustomerDetails.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchCustomerDetails.fulfilled, (state, action) => {
        state.loading = false
        state.selectedCustomer = action.payload
      })
      .addCase(fetchCustomerDetails.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Add credits
    builder
      .addCase(addCustomerCredits.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(addCustomerCredits.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(addCustomerCredits.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Update tier
    builder
      .addCase(updateCustomerTier.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCustomerTier.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(updateCustomerTier.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Update status
    builder
      .addCase(updateCustomerStatus.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateCustomerStatus.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(updateCustomerStatus.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Delete customer
    builder
      .addCase(deleteCustomer.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(deleteCustomer.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(deleteCustomer.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })

    // Reset password
    builder
      .addCase(resetCustomerPassword.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(resetCustomerPassword.fulfilled, (state) => {
        state.loading = false
      })
      .addCase(resetCustomerPassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  }
})

export const { clearAdminCustomersError, clearSelectedCustomer } = adminCustomersSlice.actions
export default adminCustomersSlice.reducer
